import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import { FORM_ERROR } from 'final-form';
import { Field, Form } from 'react-final-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import Select from 'react-select';
import { STEPS } from './commonStepControl';
import { useGuidedSetupContext } from './guidedSetupContext';
import IndustrySelect, { mapCodeToOption } from './IndustrySelect';
import { ProgressBar, TrackedSteps } from './ProgressBar';
import { PageHeader } from '../../forms/GenericFormComponents';
import { ProfitRoverPrimaryButton } from '../../forms/ProfitRoverButtons';
import HeaderAwarePage from '../../generic/HeaderAwarePage';
import Header from '../../header/header';
import { countryDropdownOption, countryDropdownOptions } from '../../util/location/dropdownOptions';
import { useUpdateCompanyInfo, useUpdateUserInfo } from '../../../data-access/mutation/users';
import { useCustomerInfo, useUserInfo } from '../../../data-access/query/users';
import { isValidWebsiteAddress } from '../../../utils/validators';
import './tell-about-yourself.scss';
import { CenteredProfitRoverSpinner } from '../../spinner/ProfitRoverSpinner';

const nonEmptyStringValidator = value => ((value ?? '') === '' ? FORM_ERROR : undefined);

// Validator for required dropdowns (expecting an object { value, label })
const nonNullOptionValidator = value => (value == null ? FORM_ERROR : undefined);

const TellAboutYourself = () => {
  const {
    guidedSetup: { naics_industry: initialIndustry, scheduled_free_call: scheduledFreeCall },
    stepState: { setStep },
    updateGuidedSetup,
  } = useGuidedSetupContext();

  const { data: userInfo = {}, isLoading: isLoadingUser } = useUserInfo();
  const { data: customerInfo = {}, isLoading: isLoadingCustomer } = useCustomerInfo();

  const isLoading = isLoadingUser || isLoadingCustomer;

  const updateUserInfo = useUpdateUserInfo();
  const updateCompanyInfo = useUpdateCompanyInfo();

  const initialFirstName = userInfo?.first_name ?? null;
  const initialLastName = userInfo?.last_name ?? null;

  const initialCompanyName = customerInfo?.overview?.name ?? null;
  const initialWebsite = customerInfo?.overview?.website ?? null;
  const initialPhoneNumber = customerInfo?.overview?.phone ?? null;
  const initialCountryCode = customerInfo?.overview?.country ?? null;

  const initialIndustryOption = React.useMemo(
    () => (initialIndustry != null ? mapCodeToOption(initialIndustry) : null),
    [initialIndustry],
  );

  const initialCountryOption = React.useMemo(
    () => (initialCountryCode != null ? countryDropdownOption(initialCountryCode) : null),
    [initialCountryCode],
  );

  const onSubmit = async (formState, form) => {
    const {
      'first-name': firstName,
      'last-name': lastName,
      'company-name': companyName,
      'phone-number': phoneNumber,
      country,
      website,
    } = formState;

    const isDirtyField = field => form.getFieldState(field)?.dirty;

    const userInfoUpdates = {};
    if (isDirtyField('first-name')) {
      userInfoUpdates.firstName = firstName;
    }
    if (isDirtyField('last-name')) {
      userInfoUpdates.lastName = lastName;
    }

    const companyInfoUpdates = {};
    if (isDirtyField('company-name')) {
      companyInfoUpdates.companyName = companyName ?? null;
      localStorage.setItem('customer_name', companyName);
    }
    if (isDirtyField('website')) {
      companyInfoUpdates.website = website ?? null;
    }
    if (isDirtyField('phone-number')) {
      companyInfoUpdates.phone = phoneNumber ?? null;
    }
    if (isDirtyField('country')) {
      companyInfoUpdates.country = country?.value ?? null;
    }

    // Only make requests if there are changes
    const promises = [];
    if (Object.keys(userInfoUpdates).length > 0) {
      promises.push(updateUserInfo.mutateAsync(userInfoUpdates));
    }

    if (Object.keys(companyInfoUpdates).length > 0) {
      promises.push(updateCompanyInfo.mutateAsync(companyInfoUpdates));
    }

    if (isDirtyField('industry')) {
      promises.push(updateGuidedSetup({ naics_industry: formState.industry.value }));
    }

    await Promise.allSettled(promises);

    setStep(scheduledFreeCall ? STEPS.THREE : STEPS.TWO);
  };

  if (isLoading) {
    return <CenteredProfitRoverSpinner />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        'first-name': initialFirstName,
        'last-name': initialLastName,
        'company-name': initialCompanyName,
        website: initialWebsite,
        'phone-number': initialPhoneNumber,
        industry: initialIndustryOption,
        country: initialCountryOption,
      }}
      render={props => {
        const { handleSubmit, submitting, invalid, values } = props;
        const selectedCountryCode = values.country?.value;

        const isSubmitDisabled = isLoading || invalid || submitting;

        return (
          <HeaderAwarePage>
            <Header />

            <ProgressBar activeStep={TrackedSteps.ABOUT_YOU} />
            <form className="industry-setup container py-5" onSubmit={handleSubmit}>
              <PageHeader className="mb-4">
                <h3>Tell us a little about yourself</h3>
              </PageHeader>

              <div className="simple-questions">
                <div className="prompt">What is your name?</div>
                <div className="side-by-side">
                  <div className="labelled-field">
                    <label htmlFor="first-name">
                      First Name*
                      <Field
                        className="text-field"
                        name="first-name"
                        component="input"
                        placeholder="First"
                        validate={nonEmptyStringValidator}
                        autoFocus
                      />
                    </label>
                  </div>
                  <div className="labelled-field">
                    <label htmlFor="last-name">
                      Last Name*
                      <Field
                        className="text-field"
                        name="last-name"
                        component="input"
                        placeholder="Last"
                        validate={nonEmptyStringValidator}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="simple-questions">
                <div className="prompt">What is the name of your company and where are you located?*</div>
                <div className="side-by-side">
                  <div className="labelled-field">
                    <label htmlFor="company-name">
                      Company Name*
                      <Field
                        className="text-field"
                        name="company-name"
                        component="input"
                        placeholder="Company Name"
                        validate={nonEmptyStringValidator}
                      />
                    </label>
                  </div>
                  <div className="labelled-field">
                    <label htmlFor="country">
                      Country*
                      <Field
                        name="country"
                        validate={nonNullOptionValidator}
                        isEqual={_.isEqual}
                        render={({ input: { onChange, value }, meta: { invalid: inputInvalid, touched } }) => {
                          return (
                            <Select
                              inputId="country"
                              value={value}
                              onChange={onChange}
                              options={countryDropdownOptions()}
                              className={classnames({ 'is-invalid': touched && inputInvalid })}
                            />
                          );
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="simple-questions">
                <div className="prompt">What industry are you in?*</div>
                <div className="dropdown-area">
                  <label htmlFor="industry" className="sr-only">
                    Your Industry
                  </label>
                  <Field
                    name="industry"
                    validate={nonNullOptionValidator}
                    isEqual={_.isEqual}
                    render={({ input: { onChange, value }, meta: { invalid: inputInvalid, touched } }) => {
                      return (
                        <IndustrySelect
                          inputId="industry"
                          value={value}
                          onChange={onChange}
                          className={classnames({ 'is-invalid': touched && inputInvalid })}
                        />
                      );
                    }}
                  />
                </div>
              </div>

              <div className="simple-questions">
                <div className="prompt">What is your company website?</div>
                <label htmlFor="website" className="sr-only">
                  Your Website{' '}
                </label>
                <Field
                  className="text-field"
                  name="website"
                  validate={value => {
                    if (value && !isValidWebsiteAddress(value)) {
                      return FORM_ERROR;
                    }
                    return undefined;
                  }}
                  render={renderProps => {
                    const { invalid: inputInvalid, touched } = renderProps.meta;

                    return (
                      <input
                        id="website"
                        type="text"
                        className={classnames('text-field', { 'is-invalid': touched && inputInvalid })}
                        placeholder="Optional"
                        {...renderProps.input}
                      />
                    );
                  }}
                />
              </div>

              <div className="simple-questions">
                <div className="prompt">Phone number</div>
                <label htmlFor="phone-number" className="sr-only">
                  Your Phone Number
                </label>
                <Field
                  name="phone-number"
                  validate={value => {
                    if (value && !isValidPhoneNumber(value)) {
                      return FORM_ERROR;
                    }
                    return undefined;
                  }}
                  selectedCountryCode={selectedCountryCode}
                  render={renderProps => {
                    const { invalid: inputInvalid, touched } = renderProps.meta;
                    const { selectedCountryCode: currentSelectedCountryCode } = renderProps;

                    return (
                      <PhoneInput
                        id="phone-number"
                        className={classnames({ 'is-invalid': touched && inputInvalid })}
                        initialValueFormat="national"
                        defaultCountry={currentSelectedCountryCode}
                        placeholder="Optional"
                        {...renderProps.input}
                      />
                    );
                  }}
                />
              </div>

              <div className="py-3">
                <ProfitRoverPrimaryButton type="submit" disabled={isSubmitDisabled}>
                  Next
                </ProfitRoverPrimaryButton>
              </div>
            </form>
          </HeaderAwarePage>
        );
      }}
    />
  );
};

export default TellAboutYourself;
